import { useAssignmentReplacePlaceholderAssignment } from 'api/assignment/assignment';
import { AssignmentViewModel } from 'api/model';
import KeyValueList from 'components/KeyValueList';
import ConfirmModal from 'components/Modal/ConfirmModal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useModalStack from 'contexts/modal/useModalStack';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatDateTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import { showException } from 'utils/exception-helper';

type Props = {
  currentAssignment: AssignmentViewModel;
  selectedAssignment: AssignmentViewModel;
  onCancel: () => void;
};

const ConfirmReplacePlaceholderCarAssignmentModal: React.FC<Props> = ({
  currentAssignment,
  selectedAssignment,
  onCancel,
}) => {
  const navigate = useNavigate();
  const modalStack = useModalStack();

  const replacePlaceholderAssignmentCall =
    useAssignmentReplacePlaceholderAssignment();

  const estimatedEndTimeDiffInMillisFromCurrentAssignmentEndTime =
    useMemo(() => {
      const currentAssignmentEstimatedStartTime =
        currentAssignment.assignment.estimatedStartTime;
      const currentAssignmentEstimatedDuration =
        currentAssignment.assignment.estimatedDuration;
      const selectedAssignmentEstimatedStartTime =
        selectedAssignment.assignment.estimatedStartTime;
      const selectedAssignmentEstimatedDuration =
        selectedAssignment.assignment.estimatedDuration;
      if (
        !currentAssignmentEstimatedStartTime ||
        !currentAssignmentEstimatedDuration ||
        !selectedAssignmentEstimatedStartTime ||
        !selectedAssignmentEstimatedDuration
      ) {
        return undefined;
      }
      const currentAssignmentEstimatedEndTime = new Date(
        currentAssignmentEstimatedStartTime.getTime() +
          currentAssignmentEstimatedDuration
      );
      const selectedAssignmentEstimatedEndTime = new Date(
        selectedAssignmentEstimatedStartTime.getTime() +
          selectedAssignmentEstimatedDuration
      );

      return (
        selectedAssignmentEstimatedEndTime.getTime() -
        currentAssignmentEstimatedEndTime.getTime()
      );
    }, [
      currentAssignment.assignment.estimatedDuration,
      currentAssignment.assignment.estimatedStartTime,
      selectedAssignment.assignment.estimatedDuration,
      selectedAssignment.assignment.estimatedStartTime,
    ]);

  const newRequiredStartTimeToMeetEstimatedEndTime = useMemo(() => {
    const selectedAssignmentEstimatedStartTime =
      selectedAssignment.assignment.estimatedStartTime;
    if (
      !selectedAssignmentEstimatedStartTime ||
      !estimatedEndTimeDiffInMillisFromCurrentAssignmentEndTime
    ) {
      return undefined;
    }

    return new Date(
      selectedAssignmentEstimatedStartTime.getTime() -
        estimatedEndTimeDiffInMillisFromCurrentAssignmentEndTime
    );
  }, [
    estimatedEndTimeDiffInMillisFromCurrentAssignmentEndTime,
    selectedAssignment.assignment.estimatedStartTime,
  ]);

  const replacePlaceholderAssignment = async () => {
    try {
      await replacePlaceholderAssignmentCall.mutateAsync({
        params: {
          placeholderAssignmentId: currentAssignment.assignment.assignmentID,
          newAssignmentId: selectedAssignment.assignment.assignmentID,
          newEstimatedStartTime: newRequiredStartTimeToMeetEstimatedEndTime,
        },
      });
      // Clear all modals in a single state update to avoid
      // re-mounting the case modal (which would try to fetch
      // the now-deleted placeholder case), then navigate to the new case.
      modalStack.popAll();
      navigate(`/sok/${selectedAssignment.case.caseID}`);
    } catch (error) {
      showException(error);
    }
  };

  return (
    <ConfirmModal
      title="Bekräfta tilldelning"
      content={
        replacePlaceholderAssignmentCall.isLoading ? (
          <LoadingSpinner>Tilldelar uppdrag...</LoadingSpinner>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxWidth: 768,
            }}
          >
            <p>
              {`Är du säker på att du vill tilldela ${selectedAssignment.case.registrationNumber} till ${currentAssignment.assignment.assignedTo?.name}?`}
            </p>
            <p>
              Utfyllnadsuppdraget kommer att tas bort och ersättas med det nya
              uppdraget.
            </p>
            <p>
              <KeyValueList
                colonKey
                rows={[
                  {
                    key: 'Ny Bokat till-tid',
                    value: `${formatDateTime(currentAssignment.assignment.estimatedStartTime)}
              (tidigare ${formatDateTime(selectedAssignment.assignment.bookedTo)})`,
                  },
                  {
                    key: 'Ny Estimerad starttid',
                    value: `${formatDateTime(currentAssignment.assignment.estimatedStartTime)}
              (tidigare ${selectedAssignment.assignment.estimatedStartTime ? formatDateTime(selectedAssignment.assignment.estimatedStartTime) : 'ej satt'})`,
                  },
                  {
                    key: 'Ny Estimerad uppdragstid',
                    value: `${getHoursAndMinutesFromMillisecondsString(
                      currentAssignment.assignment.estimatedDuration ?? 0
                    )}
                    (tidigare ${
                      selectedAssignment.assignment.estimatedDuration
                        ? getHoursAndMinutesFromMillisecondsString(
                            selectedAssignment.assignment.estimatedDuration
                          )
                        : 'ej satt'
                    })`,
                  },
                ]}
              />
            </p>
          </div>
        )
      }
      onConfirm={async () => {
        await replacePlaceholderAssignment();
      }}
      onCancel={
        replacePlaceholderAssignmentCall.isLoading ? undefined : onCancel
      }
    />
  );
};

export default ConfirmReplacePlaceholderCarAssignmentModal;
